import { queryProductionUnitList } from '@/api/ou';
import { Col, Divider, FormInstance } from 'antd';
import { FC, useEffect, useMemo } from 'react';
import { CurrentDataProps } from '../..';
import { cloneDeep, isNil, uniqBy } from 'lodash-es';
import {
  Form,
  Input,
  Modal,
  Select,
  Tag,
  CustomFilter,
  Tabs,
  MXTabsProps,
  Tree,
  message,
  Button,
} from '@maxtropy/components';
import {
  V2EnergyAnalysisUnitConfGroupLinkedOuAllPostResponse,
  apiV2ComprehensiveLabelListPost,
  apiV2CustomerUserListByStaffouPost,
  apiV2EnergyAnalysisUnitConfGroupAddPost,
  apiV2EnergyAnalysisUnitConfGroupLinkedOuAllPost,
  apiV2EnergyAnalysisUnitConfGroupUpdatePost,
  apiV2EnergyConsumptionEvaluationAnalysisUnitMovePost,
  apiV2EnergyConsumptionEvaluationAnalysisUnitUnitConfGroupListPost,
  apiV2EnergyUnitCopyPost,
  apiV2OuPagePost,
} from '@maxtropy/device-customer-apis-v2';
import { useCallback, useState, ReactText } from 'react';
import { Paging, Table, usePaging, EllipsisSpan } from '@maxtropy/components';
import { useQuery } from '../../../../../utils/utils';
import { mcidItem } from '@/api/energyConsumption';
import { OuStatusDisplay, OuStatus } from '@/shared/const';
import { V2OuPagePostRequest } from '@maxtropy/device-customer-apis-v2';
import styles from './index.module.scss';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useRequest } from 'ahooks';

interface FilterParams {
  nameOrSerialNum?: string;
  mcids?: string[];
  unitId?: number;
}

export interface UnitModalRef {
  selectedRows: OuItem[];
}
interface OuItem {
  id?: number;
  serialNumber?: string;
  name?: string;
  status?: number;
  customerName?: string;
  customerMcid?: string;
  tenantMcid?: string;
  isLinked?: boolean;
}

type SearchParams = Omit<V2OuPagePostRequest, 'page' | 'size'>;
interface AddChildModalProps {
  visible: boolean;
  onCancel: () => void;
  isAdd: boolean;
  // { name, currentId }, 其中 name 和 currentId 分别为当前用能单元某个节点的 name 和 unitConfId
  // 如果是最顶上的添加一级用能单元的话为 undefined
  currentData?: CurrentDataProps;
  // onFinish: (values: CurrentDataProps) => void;
  addEditform: FormInstance<any>;
  mcid: string;
  unitId: string | number;
  addChildModalVisible: boolean;
  addRootAnalysisGroupVisible: boolean;
  editModalVisible: boolean;
  refresh: () => void;
  mcidOptions?: mcidItem[];
}

type FormProps = {
  name: string;
  linkedOuList: number[];
  labelIdList: number[];
};

interface TiledArrayProps {
  value: string;
  label: string;
}
export type OuInfoProps = Exclude<V2EnergyAnalysisUnitConfGroupLinkedOuAllPostResponse['list'], undefined>[number];

const AddChildOrEditModal: FC<AddChildModalProps> = props => {
  const {
    visible,
    onCancel,
    isAdd,
    currentData,
    addEditform,
    mcid,
    unitId,
    refresh,
    addChildModalVisible,
    addRootAnalysisGroupVisible,
  } = props;
  const [allOuList, setAllOutList] = useState<OuInfoProps[]>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [activeKey, setActiveKey] = useState<string>('1');
  // 编辑时候初始化查到的 ou list
  const [initialLinkedOutList, setInitialLinkedOutList] = useState<OuInfoProps[]>([]);
  const [form] = Form.useForm();
  const [selectedItems, setSelectedItem] = useState<OuItem[]>([]);
  const pagingInfo = usePaging(100);
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;
  const [searchParams, setSearchParams] = useState<SearchParams>();
  const [selectedRows, setSelectedRows] = useState<OuItem[]>([]);
  const [organization, setOrganization] = useState<TiledArrayProps[]>([]);
  const [linkedOuList, setLinkedOuList] = useState<number[]>([]);
  const [treeData, setTreeData] = useState<any>([]);
  const [treeCheckedKeys, setTreeCheckedKeys] = useState<number[]>([]);
  const [disabledKeys, setDisabledKeys] = useState<number[]>([]);

  const { data: labelList, refresh: refreshLabelList } = useRequest(() => apiV2ComprehensiveLabelListPost({ id: 1 }));

  useEffect(() => {
    apiV2CustomerUserListByStaffouPost({}).then(res => {
      const map = (res?.list ?? []).map(item => ({
        label: item.name as string,
        value: item.mcid as string,
      }));
      setOrganization(map);
    });

    apiV2EnergyConsumptionEvaluationAnalysisUnitUnitConfGroupListPost({ id: Number(unitId) }).then(res => {
      setTreeData(res.list ?? []);
    });
  }, []);

  useEffect(() => {
    setLinkedOuList(selectedItems.map(item => item.id as number));
  }, [selectedItems]);

  const { data, isLoading } = useQuery(
    useCallback(async () => {
      const res = await apiV2OuPagePost({
        ...searchParams,
        mcids: searchParams?.mcids,
        ifCancel: false,
        nameOrSerialNum: searchParams?.nameOrSerialNum,
        page: pageOffset,
        size: pageSize,
      });
      if (res) setTotalCount(res.total as number);
      return res?.list;
    }, [searchParams, pageOffset, pageSize, setTotalCount])
  );

  useEffect(() => {
    setSelectedItem([...selectedItems, ...selectedRows]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRows]);

  const onReset = () => {
    form.resetFields();
    form.submit();
    setPageOffset(1);
  };

  // 获取所有子节点ID（用于父选中时强制选中所有子节点）
  const getAllChildIds = (node: any): number[] => {
    let ids: number[] = [];
    if (node.childList?.length) {
      node.childList.forEach((c: any) => {
        ids.push(c.id);
        ids = ids.concat(getAllChildIds(c));
      });
    }
    return ids;
  };

  // 获取所有父节点ID（用于子选中时禁用上级）
  const getAllParentIds = (id: number, data: any[]): number[] => {
    const result: number[] = [];
    const dfs = (list: any[], parents: number[]) => {
      for (const item of list) {
        if (item.id === id) {
          result.push(...parents);
        } else if (item.childList?.length) {
          dfs(item.childList, [...parents, item.id]);
        }
      }
    };
    dfs(data, []);
    return result;
  };

  const selfParentIds = useMemo(() => {
    if (isNil(currentData?.currentId) || !treeData.length) return [];
    return getAllParentIds(Number(currentData?.currentId), treeData);
  }, [currentData, treeData]);

  // 根据禁用节点列表重新标记 TreeData
  const markDisabledNodes = (nodes: any[], disabledKeys: number[]): any[] => {
    return nodes.map(n => ({
      ...n,
      disabled: disabledKeys.includes(n.id) || selfParentIds.includes(n.id) || Number(currentData?.currentId) === n.id,
      childList: n.childList ? markDisabledNodes(n.childList, disabledKeys) : [],
    }));
  };

  // 递归查找节点（返回节点及其父引用）
  const findNodeById = (id: number, data: any[], parent: any = null): any => {
    for (const node of data) {
      if (node.id === id) return { node, parent };
      if (node.childList?.length) {
        const found = findNodeById(id, node.childList, node);
        if (found) return found;
      }
    }
    return null;
  };

  // Tree 勾选逻辑核心
  const handleTreeCheck = (checked: any, info: any) => {
    let newChecked = [...checked.checked];
    const node = info.node;
    const nodeId = node.id;

    // 所有子节点 ID
    const childIds = getAllChildIds(node);
    // 所有父节点 ID
    const parentIds = getAllParentIds(nodeId, treeData);

    // === 1. 勾选父节点 → 自动勾选所有子节点，并锁定子节点 ===
    if (info.checked && node.childList?.length) {
      newChecked = Array.from(new Set([...newChecked, ...childIds]));
    }

    // === 2. 取消父节点 → 自动取消所有子节点 ===
    if (!info.checked && node.childList?.length) {
      newChecked = newChecked.filter(id => !childIds.includes(id));
    }

    // === 3. 勾选子节点 → 禁用所有上级节点 ===
    if (info.checked && parentIds.length) {
      setDisabledKeys(prev => Array.from(new Set([...prev, ...parentIds])));
    }

    // === 4. 取消子节点 → 若所有同级都未选，则解除上级禁用 ===
    if (!info.checked && parentIds.length) {
      parentIds.forEach(pid => {
        const foundParent = findNodeById(pid, treeData);
        if (foundParent?.node?.childList) {
          const siblings = foundParent.node.childList.map((c: any) => c.id);
          const hasCheckedSibling = siblings.some((sid: number) => newChecked.includes(sid));
          if (!hasCheckedSibling) {
            setDisabledKeys(prev => prev.filter(k => k !== pid));
          }
        }
      });
    }

    setTreeCheckedKeys(newChecked);
  };

  const onFinishSearch = (val: FilterParams) => {
    const params: SearchParams = {
      nameOrSerialNum: val.nameOrSerialNum,
      mcids: val.mcids,
    };
    setSearchParams(params);
    setPageOffset(1);
  };
  const onCheck = (checkedKeys: any) => {
    setTreeCheckedKeys([...checkedKeys.checked]);
  };
  const columns = [
    {
      title: '运营单元编号',
      dataIndex: 'serialNumber',
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '运营单元运营名称',
      dataIndex: 'name',
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '状态',
      dataIndex: 'status',
      ellipsis: { showTitle: true },
      render: (v: OuStatus) => <EllipsisSpan value={OuStatusDisplay[v]} />,
    },
    {
      title: '所属组织',
      dataIndex: 'customerName',
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
  ];

  const onSelectChange = (rowKeys: ReactText[], rows: OuItem[]) => {
    const data = rows.filter(item => !!item).filter(item => !selectedItems?.map(s => s.id).includes(item.id));
    setSelectedRows(data);
  };

  const rowSelection = {
    selectedRowKeys: (selectedItems || []).map(item => item.id as number),
    getCheckboxProps: (record: OuItem) => ({ disabled: selectedItems?.map(s => s.id).includes(record.id) }),
    onChange: onSelectChange,
  };

  const ouList = useMemo(() => {
    // 所有的 ou, 包含有权限的和无权限的
    return uniqBy([...allOuList, ...initialLinkedOutList], 'id');
  }, [allOuList, initialLinkedOutList]);

  const onFinish = (values: FormProps) => {
    if (selectedItems.length === 0) {
      Modal.warning({ content: '请选择运营单元' });
      return;
    }
    const filterLinkedOuList = ouList
      ?.filter(o => linkedOuList.includes(o.id as number))
      ?.map(v => ({ ouId: v.id!, mcid: v.mcid!, tenantMcid: v.tenantMcid! }));
    setSubmitting(true);
    if (addChildModalVisible || addRootAnalysisGroupVisible) {
      // 添加子组或者总分析组
      apiV2EnergyAnalysisUnitConfGroupAddPost({
        name: values.name,
        parentId: addChildModalVisible ? Number(currentData?.currentId) : 0,
        unitId: unitId.toString(),
        linkedOuList: filterLinkedOuList,
        labelIdList: values.labelIdList,
      })
        .then(() => {
          refresh();
          addEditform.resetFields();
          onCancel();
        })
        .finally(() => {
          setSubmitting(false);
          // onCancel();
        });
    } else {
      // 编辑
      apiV2EnergyAnalysisUnitConfGroupUpdatePost({
        energyUnitGroupId: currentData!.currentId!.toString(),
        name: values.name,
        beforeLinkedOuList: initialLinkedOutList.map(v => ({ mcid: v.mcid!, ouId: v.id!, tenantMcid: v.tenantMcid! })),
        linkedOuList: filterLinkedOuList,
        labelIdList: values.labelIdList,
      })
        .then(() => {
          refresh();
          addEditform.resetFields();
          onCancel();
        })
        .finally(() => {
          setSubmitting(false);
          // onCancel();
        });
    }
  };

  useEffect(() => {
    // 编辑时候设置初始值等
    if (currentData && !isAdd) {
      console.log('labelIdList', currentData.labelIdList);
      addEditform.setFieldsValue({ name: currentData.name });
      apiV2EnergyAnalysisUnitConfGroupLinkedOuAllPost({ energyUnitId: currentData.currentId }).then(res => {
        const linkedOuList = (res.list ?? []).map(v => v?.id as number);
        setSelectedItem(res.list as any);
        setLinkedOuList(linkedOuList);
        // 设置初始化的 oulist
        setInitialLinkedOutList(res.list ?? []);
      });
    }
  }, [addEditform, currentData, isAdd]);

  // 查询所有 ou
  useEffect(() => {
    queryProductionUnitList({ page: 1, size: 500 }).then(res => {
      setAllOutList(res.list as any);
    });
  }, [mcid]);

  const copyBatch = () => {
    setSubmitting(true);
    apiV2EnergyUnitCopyPost({
      parentId: Number(currentData?.currentId),
      copyEnergyUnitIds: treeCheckedKeys,
    })
      .then(res => {
        message.success('复制成功');
        refresh();
        addEditform.resetFields();
        onCancel();
      })
      .finally(() => setSubmitting(false));
  };

  const moveBatch = () => {
    setSubmitting(true);
    apiV2EnergyConsumptionEvaluationAnalysisUnitMovePost({
      energyUnitId: Number(currentData?.currentId),
      energyUnitIds: treeCheckedKeys,
    })
      .then(res => {
        message.success('移入成功');
        refresh();
        addEditform.resetFields();
        onCancel();
      })
      .finally(() => setSubmitting(false));
  };

  const items: MXTabsProps['items'] = [
    {
      key: '1',
      label: '创建',
    },
    {
      key: '2',
      label: '复制',
    },
    {
      key: '3',
      label: '移入已有用能单元',
    },
  ];

  return (
    <Modal
      title="用能单元"
      open={visible}
      size="big"
      destroyOnClose
      bodyScroll
      contentClassName={styles.contentWrapper}
      onOk={() => {
        activeKey === '1' ? addEditform.submit() : activeKey === '2' ? copyBatch() : moveBatch();
      }}
      onCancel={() => {
        addEditform.resetFields();
        onCancel();
      }}
      // 新增需求，蒙层外无法关闭
      maskClosable={false}
      // 设置提交后禁用提交按钮, 防止重复提交
      okButtonProps={{
        disabled: submitting,
      }}
    >
      <Tabs activeKey={activeKey} items={items} onChange={(key: string) => setActiveKey(key)} />
      {activeKey === '1' ? (
        <>
          <div className={styles.content}>
            <Form preserve={false} form={addEditform} onFinish={onFinish} style={{ marginLeft: 24 }}>
              <Form.Item validateFirst name="name" label="用能单元名称" rules={[{ required: true, message: '请输入' }]}>
                <Input placeholder="请输入" style={{ maxWidth: 474 }} />
              </Form.Item>
              <Form.Item label="包含运营单元">
                {selectedItems?.length ? (
                  selectedItems?.map(item => {
                    return (
                      <Tag
                        key={item.id}
                        style={{ marginBottom: 4 }}
                        closeIcon={true}
                        onClose={() => {
                          const deepCopy = cloneDeep(selectedItems);
                          const findIndex = selectedItems.findIndex((i: OuItem) => i.id === item.id);
                          deepCopy.splice(findIndex, 1);
                          setSelectedItem(deepCopy);
                        }}
                      >
                        {item.name}
                      </Tag>
                    );
                  })
                ) : (
                  <div className={styles.specialTip}>
                    <InfoCircleOutlined
                      style={{ color: 'var(--mx-warning-color)', marginRight: 4, fontSize: '16px' }}
                    />
                    请在下方列表中选择
                  </div>
                )}
              </Form.Item>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Form.Item label="标签" name="labelIdList" style={{ marginBottom: 0 }}>
                  <Select
                    placeholder="请选择"
                    options={labelList?.list?.map(item => ({ value: item.id, label: item.name }))}
                    allowClear
                    mode="multiple"
                    showSearch
                    optionFilterProp="label"
                    style={{ width: 474 }}
                  />
                </Form.Item>
                <Button onClick={() => refreshLabelList()}>刷新</Button>
                <Button
                  type="link"
                  style={{ padding: 0 }}
                  onClick={() => {
                    window.open('/dc/circuit/labelsManage?labelId=1', '_blank');
                  }}
                >
                  新建标签 &gt;&gt;
                </Button>
              </div>
            </Form>
          </div>
          <Divider></Divider>
          <CustomFilter colSpan={8} form={form} onFinish={val => onFinishSearch(val as FilterParams)} onReset={onReset}>
            <Form.Item name="nameOrSerialNum" label="运营单元名称">
              <Input placeholder={'请输入名称查询'} />
            </Form.Item>
            <Form.Item name="mcids" label="所属组织">
              <Select placeholder="请选择" allowClear options={organization} mode="multiple" />
            </Form.Item>
          </CustomFilter>
          <Table
            rowKey="id"
            rowSelection={{
              type: 'checkbox',
              ...rowSelection,
            }}
            loading={isLoading}
            showSelectedCount
            scroll={{ y: 227 }}
            selectedCount={selectedItems.length}
            columns={columns}
            dataSource={data}
          >
            <Paging pagingInfo={pagingInfo} />
          </Table>
        </>
      ) : activeKey === '2' ? (
        <>
          <Tree
            checkable
            className={styles.treeBox}
            defaultExpandAll
            selectable={false}
            checkStrictly={true}
            onCheck={onCheck}
            fieldNames={{ title: 'name', children: 'childList', key: 'id' }}
            treeData={treeData}
          />
          <div style={{ color: 'var(--mx-text-secondary-color)' }}>
            将所选单元按当前生效配置复制为
            <span style={{ color: 'var(--mx-primary-color)' }}>【{currentData?.name ?? '--'}】</span>子级单元
          </div>
        </>
      ) : (
        <>
          <Tree
            checkable
            className={styles.treeBox}
            defaultExpandAll
            selectable={false}
            checkStrictly={true}
            onCheck={handleTreeCheck}
            checkedKeys={treeCheckedKeys}
            fieldNames={{ title: 'name', children: 'childList', key: 'id' }}
            treeData={markDisabledNodes(treeData, disabledKeys)}
          />
          <div style={{ color: 'var(--mx-text-secondary-color)' }}>
            将所选单元按当前生效配置移入
            <span style={{ color: 'var(--mx-primary-color)' }}>【{currentData?.name ?? '--'}】</span>子级单元
          </div>
        </>
      )}
    </Modal>
  );
};

export default AddChildOrEditModal;
